<template>
  <div class="relative w-full">
    <div v-if="loading" class="absolute left-0 top-10 w-full">
      <TheLoader />
    </div>
    <div v-else data-testid="videos-tab-content">
      <div v-if="!isViewMoreActive" class="flex flex-col gap-y-5">
        <FiltersSection
          class="lg:hidden"
          data-testid="video-filters"
          :categories="filters"
          :activated-categories="filters"
          @change-categories="changeCategories"
        />
        <VideosByTagsSection
          v-for="sectionData in filteredVideoSections"
          :key="sectionData.title"
          :class="{ hidden: !isSectionFilterSelected(sectionData.title) }"
          :section-data="sectionData"
          @play="handleVideoClick"
          @view-more-click="viewMoreClick"
        />
      </div>
      <div
        v-else
        class="flex flex-col items-center"
        data-testid="view-more-section"
      >
        <div class="mb-6 flex w-full flex-col items-start">
          <div
            data-testid="back-button"
            class="mb-[18px] flex cursor-pointer items-center gap-x-1.5 text-lg font-semibold"
            @click="backAction"
          >
            <IconArrowBack class="h-[18px] w-[18px]" />
            Back
          </div>
          <div class="text-sm font-semibold" data-testid="results-message">
            Showing {{ viewMoreData.total }} results for
            {{ viewMoreData.title }}
          </div>
        </div>
        <div
          data-testid="view-more-videos"
          class="grid w-full grid-cols-1 gap-x-2 gap-y-8 md:w-auto md:grid-cols-[repeat(2,minmax(362px,489px))] lg:grid-cols-[repeat(3,minmax(194px,290px))] xl:grid-cols-[repeat(4,minmax(217px,247px))] 2xl:grid-cols-[repeat(4,minmax(275px,_1fr))]"
        >
          <VideoCard
            v-for="(video, i) in viewMoreData.videos"
            :key="video.id"
            class="max-w-full shrink-0 md:max-w-full"
            :index="i"
            :slug="video.slug"
            :title="video.title"
            :excerpt="video.excerpt"
            :duration="video.duration"
            :published-date="video.publishedDate"
            :cover-url="video.backgroundImage"
            :vimeo-id="video.vimeoId"
            :preview-vimeo-id="video.previewVimeoId"
            @play="handleVideoClick(video)"
          />
        </div>
        <CommonButton
          v-if="viewMoreData.hasNextPage"
          class="mt-6 !w-fit py-3"
          :show-spinner="true"
          :disabled="loadingMore"
          @click="loadMore"
        >
          Load More
        </CommonButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { logError } from '~/utils/log-error'
import VideoCard from '~/components/portals/VideoCard.vue'
import VideosByTagsSection from '~/components/market-insights/VideosByTagsSection.vue'
import TheLoader from '~/components/TheLoader.vue'
import CommonButton from '~/components/common/Button.vue'
import FiltersSection from '~/components/price-assessments/FiltersSection.vue'
import useAuthentication from '~/composables/useAuthentication'
import IconArrowBack from '~/assets/icons/icon-arrow-back.svg'
import type { TGeneral } from '~/src/types/common'
const emit = defineEmits(['toggle-view-more', 'play-video'])

const props = withDefaults(
  defineProps<{
    product: string
    categoriesVideos?: string[]
  }>(),
  {
    categoriesVideos: null,
  },
)

const WEBINARS = 'Webinars'
const ONE_TO_ONE_VIDEOS = '101 Videos'
const INTERVIEWS = 'Interviews'
const RELATED_CONFERENCE = 'Related Conference'

const filters = ref([
  { name: WEBINARS, slug: 'webinars' },
  { name: ONE_TO_ONE_VIDEOS, slug: 'benchmark-101' },
  { name: INTERVIEWS, slug: 'interviews' },
  { name: RELATED_CONFERENCE, slug: 'related-conference' },
])

const selectedFilters = ref(filters.value)
const loading = ref(true)
const loadingMore = ref(false)

const viewMoreData = ref<TGeneral>({
  title: '',
  videos: [],
  tags: [],
  excludeTags: false,
  nextPageHash: null,
  total: 0,
})

const webinarsData = ref<TGeneral>({
  title: WEBINARS,
  videos: [],
  tags: ['benchmark-webinars'],
  excludeTags: false,
  total: 0,
})

const oneToOneData = ref<TGeneral>({
  title: ONE_TO_ONE_VIDEOS,
  videos: [],
  tags: ['benchmark-101'],
  excludeTags: false,
  total: 0,
})

const interviewData = ref<TGeneral>({
  title: INTERVIEWS,
  videos: [],
  tags: ['power-players', 'analyst-insights'],
  excludeTags: false,
  total: 0,
})

const relatedData = ref<TGeneral>({
  title: RELATED_CONFERENCE,
  videos: [],
  tags: [
    'benchmark-webinars',
    'benchmark-101',
    'power-players',
    'analyst-insights',
  ],
  excludeTags: true,
  total: 0,
})

const videoSections = ref([
  webinarsData.value,
  oneToOneData.value,
  interviewData.value,
  relatedData.value,
])

const isViewMoreActive = computed(() => !!viewMoreData.value.total)
const categories = computed(() => props.categoriesVideos ?? [props.product])
const { $graphqlClient } = useNuxtApp()
const fetchVideos = async () => {
  try {
    loading.value = true
    const { webinars, oneToOne, interviews, related } =
      await $graphqlClient.getMarketInsightsVideos(
        webinarsData.value.tags,
        oneToOneData.value.tags,
        interviewData.value.tags,
        categories.value,
      )
    assignVideoData(webinarsData.value, webinars)
    assignVideoData(oneToOneData.value, oneToOne)
    assignVideoData(interviewData.value, interviews)
    assignVideoData(relatedData.value, related)
  } catch (error) {
    logError(error)
  } finally {
    loading.value = false
  }
}

const handleVideoClick = (vimeoData) => {
  emit('play-video', vimeoData)
}

const viewMoreClick = async (selectedData) => {
  window?.scrollTo({ top: 0, behavior: 'smooth' })
  viewMoreData.value.title = selectedData.title || ''
  viewMoreData.value.tags = selectedData.tags
  viewMoreData.value.total = selectedData.total
  viewMoreData.value.excludeTags = selectedData.excludeTags
  loading.value = true
  await loadMore()
  loading.value = false
}

const loadMore = async () => {
  loadingMore.value = true
  const { nodes, pageInfo } = await $graphqlClient.getVideosByTags(
    12,
    viewMoreData.value.tags,
    categories.value,
    viewMoreData.value.excludeTags,
    viewMoreData.value.nextPageHash,
  )
  viewMoreData.value.videos = [...viewMoreData.value.videos, ...nodes]
  viewMoreData.value.nextPageHash = pageInfo.endCursor
  viewMoreData.value.hasNextPage = pageInfo.hasNextPage
  loadingMore.value = false
}

const assignVideoData = (target, source) => {
  target.videos = source.nodes
  target.total = source.pageInfo.total
  target.nextPageHash = source.pageInfo.endCursor
}

const backAction = () => {
  viewMoreData.value = {
    title: '',
    videos: [],
    tags: [],
    excludeTags: false,
    nextPageHash: null,
    total: 0,
  }
}

const changeCategories = (selectedFiltersData) => {
  selectedFilters.value = selectedFiltersData
}

const isSectionFilterSelected = (sectionName) => {
  if (!selectedFilters.value.length) return true
  return selectedFilters.value.some((filter) => filter.name === sectionName)
}
const filteredVideoSections = computed(() =>
  videoSections.value.filter((section) => section.videos.length > 0),
)
onMounted(async () => {
  fetchVideos()
  useAuthentication(fetchVideos)
})

watch(isViewMoreActive, (isActive) => {
  emit('toggle-view-more', isActive)
})
</script>
